<template>
  <slice
    :id="block._key"
    class="slice--products slice--products--auto slice--products--grid"
    :class="{
      'grid-filter': hasFilters,
      'hide-filters': hideFilters,
      'slice--products--opened-filter': openedFilters,
    }"
    data-v-sticky-container
    @in="$emit('in')"
    @out="$emit('out')"
  >
    <transition name="filter-refresh">
      <div v-if="refreshing" key="refreshing" class="grid-filter__refreshing">
        <div v-sticky="sticky">
          <div
            class="grid-filter__refreshing__inner h4"
            data-v-sticky-refreshing-inner
          >
            <icon-loading class="mr-1" />
            <span class="text-secondary">{{ $t('loading') }}</span>
          </div>
        </div>
      </div>
    </transition>

    <div v-if="hasFilters" class="grid-filter__filters" data-v-sticky-inner>
      <Filters
        :slice-id="block._key"
        :filters="filters"
        class="pl-md-1 pr-md-1 mb-0-4 mb-md-1"
        @update="onFiltersUpdate"
        @request-reduce="reduceFilters"
        @filter-opened="onFilterOpened"
        @filter-closed="onFilterClosed"
      />
    </div>

    <div class="open-filters" :class="{ visible: hideFilters }">
      <div v-sticky="stickyOpenFilters">
        <div class="open-filters__inner" data-v-sticky-open-filters-inner>
          <button-link with-icon icon="open" @click="reduceFilters">
            <span class="text-extra-small text-uppercase w-500">{{
              $t('action.open_filters')
            }}</span>
          </button-link>
        </div>
      </div>
    </div>

    <div
      v-if="!init"
      class="grid-of-products"
      :style="{
        '--number-of-lines': block.gallery_mobile.length,
        '--number-of-lines-desktop': block.gallery.length,
      }"
    >
      <div
        v-for="i in block.gallery_mobile.length"
        :key="`mobile-${i}`"
        class="skeleton"
        :style="{
          '--product-grid-column-mobile': '1/13',
          '--product-grid-row-mobile': `${i}/${i + 1}`,
        }"
      ></div>

      <div
        v-for="i in block.gallery.length"
        :key="`desktop-${i}`"
        class="skeleton"
        :style="{
          '--product-grid-column-desktop': '1/13',
          '--product-grid-row-desktop': `${i}/${i + 1}`,
        }"
      ></div>
    </div>
    <div
      v-else-if="gridOfItems.length > 0 && !openedFilters"
      ref="gridOfProducts"
      class="grid-of-products"
      :class="`grid-of-products--${block._key}`"
    >
      <product-card
        v-for="(item, index) of gridOfItems"
        :key="item.productDocument._id"
        v-bind="cardAttributes(item, index)"
      />

      <div v-html="gridStyle" />
    </div>
    <div
      v-else-if="!openedFilters"
      ref="gridOfProducts"
      class="grid-of-products"
      :class="`grid-of-products--${block._key}`"
    >
      <div
        :style="{
          '--product-grid-column-mobile': '1/13',
          '--product-grid-row-mobile': `1/2`,
          '--product-grid-column-desktop': '1/13',
          '--product-grid-row-desktop': `1/2`,
          'min-height': '400px',
        }"
      >
        <div class="row justify-center">
          <div class="col-md-6">
            <p>
              {{ $t('search.no_result') }}
            </p>

            <p>
              <a
                href="#"
                class="link-unroll text-small d-block mt-1-5 pl-1 pl-md-0 pr-1 pr-md-0"
                @click.prevent="resetFilters"
                >{{ $t('search.filter.all_reset') }}</a
              >
            </p>
          </div>
        </div>
      </div>
    </div>

    <div
      v-waypoint="{
        active: showLoadMore,
        callback: onWaypoint,
        options: intersectionOptions,
      }"
      class="col-xs-12"
    >
      <div
        v-if="!init || refreshing"
        class="load-more d-flex justify-center align-center pt-2 pb-2"
      >
        <icon-loading class="mr-0-6" />
        <span>{{ $t('loading') }}</span>
      </div>
    </div>
  </slice>
</template>

<script>
import { mapMutations } from 'vuex'
import { isFuture } from 'date-fns'
import ListGallery from './mixins/ListGallery'

import Slice from '@/components/Slices/Slice'
import ProductCardV2 from '@/components/Product/Card.v2'
import IconLoading from '@/components/icons/Loading'
import ButtonLink from '@/components/buttons/Link'

import { instianciateProduct } from '@/entities/sanity/product'
import ShopifyProduct from '@/entities/shopify/Product'

import services from '@/services'
import ShopifyBase64 from '~/utils/shopifyBase64'

export default {
  name: 'ListShopify',
  components: {
    Slice,
    ProductCard: ProductCardV2,
    IconLoading,
    ButtonLink,
    Filters: () => ({
      component: import(
        '@/components/filters/Default' /* webpackChunkName: 'filters' */
      ),
    }),
  },
  mixins: [ListGallery],
  props: {
    block: {
      type: Object,
      required: true,
    },
    firstBlock: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    intersectionOptions: {
      root: null,
      rootMargin: '200px 0px 200px 0px',
      threshold: [0, 1],
    },
    products: [],
    init: true,
    refreshing: false,
    sellsWillOpenSoon: false,
    page: 1,
    pageSize: 50,
    loadMultiplePage: 0,
    totalPage: 0,
    hasNextPage: false,
    currentEndCursor: '',
    filters: {},
    selectedFilters: {},
    hideFilters: true,
    openedFilters: false,
    sticky: {
      containerSelector: '[data-v-sticky-container]',
      innerWrapperSelector: '[data-v-sticky-refreshing-inner]',
      resizeSensor: true,
      minWidth: 769,
    },
    stickyOpenFilters: {
      containerSelector: '[data-v-sticky-container]',
      innerWrapperSelector: '[data-v-sticky-open-filters-inner]',
      resizeSensor: true,
      minWidth: 769,
    },
  }),
  async fetch() {
    this.init = false

    this.prepareFilters()
    await this.loadShopifyCollection()

    this.init = true
  },
  computed: {
    hasFilters() {
      if (this.block.activate_filters && this.init) {
        return (
          typeof this.filters.product_type !== 'undefined' ||
          typeof this.filters.sizes !== 'undefined'
        )
      }
      return false
    },
    showLoadMore() {
      return this.hasNextPage && !this.openedFilters
    },
    gridStyle() {
      let gridTemplateRows = ''

      gridTemplateRows = this.getGridLines(this.block.gallery_mobile)
        .map((n) => {
          switch (n) {
            case 1:
              return 'var(--default-grid-height-one-per-line)'
            case 2:
              return 'var(--default-grid-height-two-per-line)'
          }
          return ''
        })
        .join(' ')
      gridTemplateRows = `grid-template-rows: ${gridTemplateRows};`

      return `
      <style>
        @media screen and (max-width: 769px) {
          .grid-of-products--${this.block._key} {
            --number-of-lines: ${this.numberOfLines.mobile};
            ${gridTemplateRows}
          }
        }

        @media screen and (min-width: 770px) {
          .grid-of-products--${this.block._key} {
            --number-of-lines-desktop: ${this.numberOfLines.desktop};
          }
        }
      </style>`
    },
  },
  mounted() {
    if (!this.$fetchState.pending && this.init) {
      this.prepareFilters()
      this.loadShopifyCollection(false, false)
    }
  },
  methods: {
    ...mapMutations({
      setScroll: 'behavior/SET_SCROLL',
    }),
    async loadShopifyCollection(
      next = false,
      scrollToTop = false,
      initialProducts = []
    ) {
      if (!this.block.collection_id) {
        return
      }

      this.refreshing = true

      try {
        const collection = await services.collectionService.get(
          this.block.collection_id,
          { pageSize: this.pageSize, filters: this.selectedFilters },
          this.$i18n.locale,
          next && this.currentEndCursor ? this.currentEndCursor : null
        )

        let products = collection.products.nodes.map(
          (p) => new ShopifyProduct(p)
        )

        if (initialProducts.length > 0) {
          products = [...initialProducts, ...products]
        }

        if (products.length === 0 && !next) {
          this.products = []
          this.refreshing = false
          this.updateGrid()
          return
        }

        if (this.loadMultiplePage > this.page) {
          this.page++
          this.hasNextPage = collection.products.pageInfo.hasNextPage
          if (this.hasNextPage) {
            this.currentEndCursor = collection.products.pageInfo.endCursor
            return this.loadShopifyCollection(true, scrollToTop, products)
          }
        }

        const shopifyIds = products.map((p) => `shopify-product-${p.id}`)

        let productDocuments = await services.sanityService
          .getProductsByShopifyIds({
            ids: shopifyIds,
            context: this.$route.params.context,
            lang: this.$i18n.locale.slice(0, 2),
          })
          .then((res) => res.map((p) => instianciateProduct(p)))

        const orderIdsIndexMap = new Map(
          products.map((p, i) => {
            return [`${ShopifyBase64.getId(p.id)}`, i]
          })
        )

        productDocuments = productDocuments.sort((a, b) => {
          return (
            orderIdsIndexMap.get(a.shopify_product.id) -
            orderIdsIndexMap.get(b.shopify_product.id)
          )
        })

        if (next && initialProducts.length === 0) {
          this.products = [...this.products, ...productDocuments]
        } else {
          this.products = productDocuments
        }

        this.hasNextPage = collection.products.pageInfo.hasNextPage
        this.currentEndCursor = collection.products.pageInfo.endCursor

        if (
          collection.products.filters &&
          collection.products.filters.length > 0
        ) {
          if (
            collection.products.filters.some(
              (f) => f.id === 'filter.p.product_type'
            )
          ) {
            if (
              !this.selectedFilters.sizes ||
              this.selectedFilters.sizes.length === 0
            ) {
              this.filters.product_type = collection.products.filters.find(
                (f) => f.id === 'filter.p.product_type'
              ).values
            }
          }

          if (
            collection.products.filters.some(
              (f) => f.id === 'filter.v.option.size'
            ) &&
            !this.block.only_product_type_filter
          ) {
            this.filters.sizes = collection.products.filters
              .find((f) => f.id === 'filter.v.option.size')
              .values.filter((s) => s.count > 0)
          } else {
            this.filters.sizes = undefined
          }
        }

        if (this.init && !next && scrollToTop) {
          if (
            this.selectedFilters &&
            Object.keys(this.selectedFilters).length > 0
          ) {
            if (
              this.$route.query.productType &&
              this.selectedFilters.productType &&
              this.$route.query.productType === this.selectedFilters.productType
            ) {
              this.$router.replace({
                query: {
                  ...this.$route.query,
                  ...this.selectedFilters,
                },
              })
            } else {
              this.$router.push({
                query: {
                  ...this.$route.query,
                  ...this.selectedFilters,
                },
              })
            }
          } else if (this.$route.query.productType) {
            this.$router.push({
              query: {
                ...this.$route.query,
                ...{ productType: undefined, sizes: undefined },
              },
            })
          }
        }

        if (collection.metafield && collection.metafield.value) {
          const d = new Date(collection.metafield.value)

          this.sellsWillOpenSoon = isFuture(d)
        } else {
          this.sellsWillOpenSoon = false
        }

        this.updateGrid()
        this.refreshing = false

        if (scrollToTop) {
          this.$nextTick().then((_) => {
            this.scrollToTop()
          })
        }
      } catch (err) {
        this.$logError.captureException(err)
      }
    },
    updateGrid() {
      const pp = this.products.map((p) => {
        return {
          type: 'product',
          productDocument: p,
        }
      })

      this.gridOfItems = pp
    },
    cardAttributes(item, i) {
      return {
        productDocument: item.productDocument,
        stock: this.block.type === 'stock',
        typeMobile: this.gridItemPositions[i]
          ? this.gridItemPositions[i].mobile.type
          : '',
        typeDesktop: this.gridItemPositions[i]
          ? this.gridItemPositions[i].desktop.type
          : '',
        style: this.gridItemPositions[i]
          ? `
          --product-grid-column-mobile: ${this.gridItemPositions[i].mobile.column};
          --product-grid-column-desktop: ${this.gridItemPositions[i].desktop.column};
          --product-grid-row-mobile: ${this.gridItemPositions[i].mobile.row};
          --product-grid-row-desktop: ${this.gridItemPositions[i].desktop.row};
          `
          : '',
        small: ['1/7', '7/13'].includes(
          this.gridItemPositions[i].mobile.column
        ),
        bisOnly: this.block.type === 'the-collection',
      }
    },
    prepareFilters() {
      if (Object.keys(this.$route.query).length > 0) {
        if (this.$route.query.productType) {
          this.selectedFilters.productType = this.$route.query.productType
        }

        if (this.$route.query.sizes) {
          this.sizes = this.$route.query.sizes
        }
      }
    },
    onFiltersUpdate(filters, fromUser = true) {
      if (filters.productType) {
        this.selectedFilters = filters
      } else {
        this.selectedFilters = {}
      }

      this.loadShopifyCollection(false, fromUser)
    },
    reduceFilters() {
      this.hideFilters = !this.hideFilters
    },
    onFilterOpened() {
      this.openedFilters = true
    },
    onFilterClosed() {
      this.openedFilters = false
    },
    async scrollToTop() {
      if (!this.$refs.gridOfProducts) {
        return
      }

      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)
      const { ScrollToPlugin } = await import(
        'gsap/ScrollToPlugin' /* webpackChunkName: 'gsap_scroll_to_plugin' */
      )
      gsap.registerPlugin(ScrollToPlugin)

      let offsetY = 0

      if (document.body.classList.contains('header-visible')) {
        offsetY += document.getElementById('header').firstChild.offsetHeight
      }

      this.setScroll(true)
      gsap.to(window, {
        duration: 0.5,
        scrollTo: {
          y: this.$refs.gridOfProducts.$el || this.$refs.gridOfProducts,
          offsetY,
        },
        onComplete: () => {
          this.setScroll(false)
        },
      })
    },
    onWaypoint(waypoint) {
      if (waypoint.going === this.$waypointMap.GOING_IN && !this.refreshing) {
        this.loadShopifyCollection(true)
      }
    },
    resetFilters() {
      this.$nuxt.$emit('reset-filters')
    },
  },
}
</script>

<style lang="scss">
.grid-filter {
  @include mq($from: tablet) {
    --filters-width: #{rem(260px)};

    display: grid;
    transition: grid-template-columns 0.6s var(--asphalte-animation-function);
    grid-template-columns: var(--filters-width) 1fr;

    .list__filter {
      width: var(--filters-width);
      transition: all 0.5s var(--asphalte-animation-function) 0.2s;
    }
  }

  @include mq($from: tablet) {
    &.hide-filters {
      grid-template-columns: 0 1fr;
      transition: grid-template-columns 0.6s var(--asphalte-animation-function)
        0.2s;

      .list__filter {
        opacity: 0;
        transform: translate(-2rem, 0);
        transition: all 0.5s var(--asphalte-animation-function);
      }
    }
  }

  .grid-of-products {
    grid-row: 1 / 2;
    grid-column: 2 / 3;
  }

  &__filters {
    grid-row: 1 / 2;
    grid-column: 1 / 2;
  }

  &__refreshing {
    position: relative;
    grid-column: 1/-1;
    grid-row: 1/-1;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: rgba($tertiary-color, 0.5);
      z-index: 250;
      pointer-events: all;
      cursor: progress;
    }

    &__inner {
      position: absolute;
      top: calc(var(--spacing) * 2);
      left: 0;
      right: 0;
      z-index: 251;
      display: flex;
      align-items: center;
      justify-content: center;

      .loader {
        --loader-width: #{rem(32px)};
      }
    }

    &.filter-refresh-enter-active,
    &.filter-refresh-leave-active {
      transition: all 0.3s;

      &::before,
      .grid-of-products__refreshing__inner {
        transition: all 0.3s;
      }
    }

    &.filter-refresh-enter,
    &.filter-refresh-leave-to {
      &::before,
      .grid-of-products__refreshing__inner {
        opacity: 0;
      }

      .grid-of-products__refreshing__inner {
        transform: translateY(-10px);
      }
    }
  }
}

.slice--products {
  &.slice--products--with-filters {
    overflow: visible;
  }

  &.slice--products--opened-filter {
    margin: 0 !important;
  }
}

.open-filters {
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  pointer-events: none;
  z-index: 10;
  transition: all 0.3s var(--asphalte-animation-function);

  @include mq($until: tablet) {
    display: none;
  }

  .btn-link {
    background-color: $tertiary-color;
    padding: calc(var(--spacing) * 0.6) var(--spacing)
      calc(var(--spacing) * 0.8);
    transition: all 0.3s var(--asphalte-animation-function);
    color: var(--secondary-color);

    @include on-hover-and-focus {
      padding: calc(var(--spacing) * 0.6) calc(var(--spacing) * 1.5)
        calc(var(--spacing) * 0.8);
      background-color: rgba($tertiary-color, 1);
      color: var(--secondary-color);
    }
  }

  .link--underline > span::after {
    bottom: rem(-2px);
  }

  &.visible {
    opacity: 1;
    pointer-events: all;
    transition: all 0.3s var(--asphalte-animation-function) 0.7s;
  }

  .is-affixed {
    .open-filters__inner {
      transition: padding 0.4s cubic-bezier(0.46, 0.01, 0.32, 1);
      width: auto !important; // SURCHARGE STICKY

      @include mq($from: tablet) {
        .header-visible & {
          padding-top: calc(var(--header-height));
        }
      }
    }
  }
}

@keyframes appear-left {
  0% {
    opacity: 0;
    transform: translateX(-30px);
  }

  100% {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
